import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Building2, BarChart3, Images, Truck, FileText, Newspaper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import ImageUpload from "@/components/admin/ImageUpload";
import RichTextEditor from "@/components/admin/RichTextEditor";

function Section({ icon: Icon, title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
      <h3 className="font-display text-lg font-bold text-[#0F1E36] flex items-center gap-2 mb-5">
        <Icon className="w-5 h-5 text-[#FF5A1F]" />
        {title}
      </h3>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testId }) {
  return (
    <div>
      <Label className="font-semibold text-sm text-[#0F1E36]">{label}</Label>
      <Input
        data-testid={testId}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1.5"
      />
    </div>
  );
}

export default function SettingsPanel() {
  const [data, setData] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [{ data: s }, { data: arts }] = await Promise.all([
        api.get("/admin/settings"),
        api.get("/admin/articles"),
      ]);
      setData(s);
      setArticles(arts || []);
    } catch {
      toast.error("Eroare la încărcare");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", data);
      toast.success("Modificările au fost salvate și sunt vizibile pe site");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data)
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );

  const c = data.company || {};
  const about = data.about || {};
  const stats = about.stats || [];
  const slides = data.hero_slides || [];
  const roadside = data.roadside || {};

  const setCompany = (k, v) => setData({ ...data, company: { ...c, [k]: v } });
  const setRoadside = (k, v) => setData({ ...data, roadside: { ...roadside, [k]: v } });
  const setAbout = (k, v) => setData({ ...data, about: { ...about, [k]: v } });
  const setStat = (i, k, v) => {
    const next = stats.map((s, idx) => (idx === i ? { ...s, [k]: v } : s));
    setData({ ...data, about: { ...about, stats: next } });
  };
  const setSlide = (i, k, v) => {
    const next = slides.map((s, idx) => (idx === i ? { ...s, [k]: v } : s));
    setData({ ...data, hero_slides: next });
  };

  return (
    <div className="space-y-6 pb-24">
      {/* Company */}
      <Section icon={Building2} title="Date firmă & contact">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Nume firmă" value={c.name} onChange={(v) => setCompany("name", v)} testId="settings-company-name" />
          <Field label="Telefon" value={c.phone} onChange={(v) => setCompany("phone", v)} />
          <Field label="Telefon asistență/urgențe" value={c.phone_emergency} onChange={(v) => setCompany("phone_emergency", v)} />
          <Field label="Email public (afișat pe site)" value={c.email} onChange={(v) => setCompany("email", v)} placeholder="opțional" />
          <Field label="Email notificări (mesaje noi)" value={c.notify_email} onChange={(v) => setCompany("notify_email", v)} testId="settings-notify-email" />
          <Field label="GPS (lat, long)" value={c.gps} onChange={(v) => setCompany("gps", v)} />
        </div>
        <div className="mt-4">
          <Field label="Adresă" value={c.address} onChange={(v) => setCompany("address", v)} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <Field label="Program Luni-Vineri" value={c.program_weekdays} onChange={(v) => setCompany("program_weekdays", v)} />
          <Field label="Program Sâmbătă" value={c.program_saturday} onChange={(v) => setCompany("program_saturday", v)} />
          <Field label="Program Duminică" value={c.program_sunday} onChange={(v) => setCompany("program_sunday", v)} />
          <Field label="Program ITP (opțional)" value={c.program_itp} onChange={(v) => setCompany("program_itp", v)} />
        </div>
        <div className="mt-4">
          <Label className="font-semibold text-sm text-[#0F1E36]">URL embed hartă Google Maps</Label>
          <Textarea
            value={c.maps_embed || ""}
            onChange={(e) => setCompany("maps_embed", e.target.value)}
            rows={2}
            className="mt-1.5 font-mono text-xs"
          />
        </div>
      </Section>

      {/* Roadside / emergency bar */}
      <Section icon={Truck} title="Bară Asistență Rutieră">
        <div className="flex items-center justify-between bg-[#F4F6F9] rounded-lg px-4 py-3 mb-4">
          <div>
            <Label className="font-semibold">Afișează bara pe site</Label>
            <p className="text-xs text-slate-500 mt-0.5">
              Activeaz-o doar dacă oferi serviciul de asistență rutieră / tractare.
            </p>
          </div>
          <Switch
            data-testid="settings-roadside-toggle"
            checked={!!roadside.enabled}
            onCheckedChange={(v) => setRoadside("enabled", v)}
          />
        </div>
        <div className="space-y-4">
          <Field label="Titlu bară" value={roadside.title} onChange={(v) => setRoadside("title", v)} />
          <div>
            <Label className="font-semibold text-sm text-[#0F1E36]">Subtitlu bară</Label>
            <Textarea
              value={roadside.subtitle || ""}
              onChange={(e) => setRoadside("subtitle", e.target.value)}
              rows={2}
              className="mt-1.5"
            />
          </div>
        </div>
      </Section>

      {/* About + stats */}
      <Section icon={BarChart3} title="Despre noi & cifre">
        <div className="space-y-4">
          <Field label="Titlu secțiune" value={about.heading} onChange={(v) => setAbout("heading", v)} />
          <div>
            <Label className="font-semibold text-sm text-[#0F1E36]">Text descriere</Label>
            <Textarea
              value={about.text || ""}
              onChange={(e) => setAbout("text", e.target.value)}
              rows={4}
              className="mt-1.5"
            />
          </div>
          <ImageUpload
            label="Imagine secțiune Despre noi"
            value={about.image_url}
            onChange={(url) => setAbout("image_url", url)}
          />
        </div>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {stats.map((s, i) => (
            <div key={i} className="bg-[#F4F6F9] rounded-xl p-4 space-y-3" data-testid={`settings-stat-${i}`}>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-500">Valoare</Label>
                  <Input value={s.value || ""} onChange={(e) => setStat(i, "value", e.target.value)} className="mt-1 bg-white" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-500">Etichetă</Label>
                  <Input value={s.label || ""} onChange={(e) => setStat(i, "label", e.target.value)} className="mt-1 bg-white" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-500">Descriere</Label>
                <Input value={s.desc || ""} onChange={(e) => setStat(i, "desc", e.target.value)} className="mt-1 bg-white" />
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Hero slides */}
      <Section icon={Images} title="Slide-uri Hero (carousel)">
        <div className="space-y-5">
          {slides.map((s, i) => (
            <div key={i} className="border border-[#E2E8F0] rounded-xl p-4 space-y-3" data-testid={`settings-slide-${i}`}>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                Slide {i + 1}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-500">Etichetă (tag)</Label>
                  <Input value={s.tag || ""} onChange={(e) => setSlide(i, "tag", e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-500">Titlu</Label>
                  <Input value={s.title || ""} onChange={(e) => setSlide(i, "title", e.target.value)} className="mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-500">Subtitlu</Label>
                <Textarea value={s.subtitle || ""} onChange={(e) => setSlide(i, "subtitle", e.target.value)} rows={2} className="mt-1" />
              </div>
              <ImageUpload
                label="Imagine fundal slide"
                value={s.image_url}
                onChange={(url) => setSlide(i, "image_url", url)}
              />
            </div>
          ))}
        </div>
      </Section>

      {/* Featured article under hero */}
      <Section icon={Newspaper} title="Articol recomandat (secțiunea de sub hero)">
        <p className="text-sm text-slate-500 mb-4">
          Alege ce apare în secțiunea „Ultimul articol din blog" de pe pagina principală.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {[
            { v: "latest", label: "Automat (cel mai nou)" },
            { v: "fixed", label: "Articol fix (ales de mine)" },
            { v: "rotate", label: "Rotație (toate pe rând)" },
          ].map((o) => {
            const active = (data.featured?.mode || "latest") === o.v;
            return (
              <button
                key={o.v}
                type="button"
                data-testid={`featured-mode-${o.v}`}
                onClick={() => setData({ ...data, featured: { ...(data.featured || {}), mode: o.v } })}
                className={`px-3 py-3 rounded-lg text-sm font-semibold border transition-colors duration-200 ${
                  active
                    ? "bg-[#FF5A1F] text-white border-[#FF5A1F]"
                    : "bg-white text-slate-600 border-[#E2E8F0] hover:border-[#FF5A1F]"
                }`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
        {data.featured?.mode === "fixed" && (
          <div className="mt-4">
            <Label className="font-semibold text-sm text-[#0F1E36]">Alege articolul afișat</Label>
            <select
              data-testid="featured-article-select"
              value={data.featured?.article_id || ""}
              onChange={(e) => setData({ ...data, featured: { ...data.featured, article_id: e.target.value } })}
              className="mt-1.5 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm bg-white"
            >
              <option value="">— alege un articol —</option>
              {articles.map((a) => (
                <option key={a.id} value={a.id}>{a.title}{a.published ? "" : " (ciornă)"}</option>
              ))}
            </select>
          </div>
        )}
        {data.featured?.mode === "rotate" && (
          <div className="mt-4 max-w-xs">
            <Label className="font-semibold text-sm text-[#0F1E36]">Schimbă articolul la fiecare (secunde)</Label>
            <Input
              type="number"
              min={3}
              data-testid="featured-rotate-seconds"
              value={data.featured?.rotate_seconds ?? 8}
              onChange={(e) => setData({ ...data, featured: { ...data.featured, rotate_seconds: parseInt(e.target.value || "8", 10) } })}
              className="mt-1.5"
            />
            <p className="text-xs text-slate-400 mt-1.5">Rulează toate articolele publicate, pe rând.</p>
          </div>
        )}
      </Section>

      {/* Terms & Conditions */}
      <Section icon={FileText} title="Termeni și Condiții (pagina /termeni)">
        <p className="text-sm text-slate-500 mb-3">
          Editează textul paginii „Termeni și Condiții". Poți folosi titluri, liste, link-uri și formatare.
        </p>
        <RichTextEditor
          key="terms-editor"
          value={data.terms_content || ""}
          onChange={(html) => setData({ ...data, terms_content: html })}
        />
      </Section>

      {/* Sticky save */}
      <div className="fixed bottom-0 left-0 right-0 lg:left-64 bg-white/95 backdrop-blur border-t border-[#E2E8F0] py-4 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-end">
          <Button
            onClick={save}
            disabled={saving}
            data-testid="admin-settings-save-button"
            className="bg-[#FF5A1F] hover:bg-[#E04B14] text-white font-semibold px-8 py-6"
          >
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-5 h-5 mr-2" />Salvează modificările</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
